/**
 * Theme Context
 *
 * React Context for managing theme state across the application.
 * Supports multiple theme categories (student, teacher) with page-specific variations.
 * Persists theme selection to localStorage with separate keys per category.
 */

import React, { createContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';

import {
  Theme,
  ThemeCategory,
  ThemeContextValue,
  PageId,
  BackgroundTheme,
  GradientDefinition,
} from '../../styles/themes/theme-types';
import {
  getTheme,
  getThemesByCategory,
  getDefaultTheme,
  isValidThemeId,
  THEME_STORAGE_KEYS,
} from '../../styles/themes/theme-registry';

/**
 * Theme Provider Props
 */
interface ThemeProviderProps {
  children: ReactNode;
  category: ThemeCategory;
  defaultThemeId?: string;
  initialPageId?: PageId;
}

/**
 * Create the theme context
 */
export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Build CSS gradient string from gradient definition
 */
function buildGradientString(gradient: GradientDefinition): string {
  const stops = gradient.stops
    .map((stop) => {
      const position = stop.position !== undefined ? ` ${stop.position}%` : '';
      return `${stop.color}${position}`;
    })
    .join(', ');

  if (gradient.type === 'linear') {
    return `linear-gradient(${gradient.angle || 0}deg, ${stops})`;
  }
  return `radial-gradient(${stops})`;
}

/**
 * Apply theme CSS variables to document root
 */
function applyThemeToCSSVariables(theme: Theme, pageBackground: BackgroundTheme): void {
  const root = document.documentElement;

  // Background
  root.style.setProperty('--theme-bg-base', pageBackground.base);
  root.style.setProperty('--theme-bg-blur', pageBackground.blur);
  root.style.setProperty('--theme-bg-backdrop-filter', pageBackground.backdropFilter);
  root.style.setProperty('--theme-bg-gradient', buildGradientString(pageBackground.gradient));

  if (pageBackground.gradientOverlay) {
    root.style.setProperty(
      '--theme-bg-gradient-overlay',
      buildGradientString(pageBackground.gradientOverlay)
    );
  } else {
    root.style.setProperty('--theme-bg-gradient-overlay', 'none');
  }

  // Text
  root.style.setProperty('--theme-text-primary', theme.text.primary);
  root.style.setProperty('--theme-text-secondary', theme.text.secondary);
  root.style.setProperty('--theme-text-tertiary', theme.text.tertiary);
  root.style.setProperty('--theme-text-muted', theme.text.muted);
  root.style.setProperty('--theme-text-inverse', theme.text.inverse);

  // Accent
  root.style.setProperty('--theme-accent-primary', theme.accent.primary);
  root.style.setProperty('--theme-accent-primary-hover', theme.accent.primaryHover);
  root.style.setProperty('--theme-accent-primary-gradient', theme.accent.primaryGradient);
  root.style.setProperty('--theme-accent-primary-gradient-hover', theme.accent.primaryGradientHover);
  root.style.setProperty('--theme-accent-secondary', theme.accent.secondary);
  root.style.setProperty('--theme-accent-secondary-hover', theme.accent.secondaryHover);
  root.style.setProperty('--theme-accent-secondary-gradient', theme.accent.secondaryGradient);
  root.style.setProperty('--theme-accent-secondary-gradient-hover', theme.accent.secondaryGradientHover);
  root.style.setProperty('--theme-accent-success', theme.accent.success);
  root.style.setProperty('--theme-accent-success-strong', theme.accent.successStrong);
  root.style.setProperty('--theme-accent-warning', theme.accent.warning);
  root.style.setProperty('--theme-accent-warning-strong', theme.accent.warningStrong);
  root.style.setProperty('--theme-accent-error', theme.accent.error);
  root.style.setProperty('--theme-accent-info', theme.accent.info);

  // Shadow
  root.style.setProperty('--theme-shadow-sm', theme.shadow.sm);
  root.style.setProperty('--theme-shadow-md', theme.shadow.md);
  root.style.setProperty('--theme-shadow-lg', theme.shadow.lg);
  root.style.setProperty('--theme-shadow-xl', theme.shadow.xl);
  root.style.setProperty('--theme-shadow-accent', theme.shadow.accent);

  // Border
  root.style.setProperty('--theme-border-light', theme.border.light);
  root.style.setProperty('--theme-border-medium', theme.border.medium);
  root.style.setProperty('--theme-border-strong', theme.border.strong);
  root.style.setProperty('--theme-border-radius', theme.border.radius);
  root.style.setProperty('--theme-border-width', theme.border.width);

  // Card
  root.style.setProperty('--theme-card-background', theme.card.background);
  root.style.setProperty('--theme-card-background-hover', theme.card.backgroundHover);
  root.style.setProperty('--theme-card-border', theme.card.border);
  root.style.setProperty('--theme-card-shadow', theme.card.shadow);
  root.style.setProperty('--theme-card-backdrop-filter', theme.card.backdropFilter);

  // Nav
  root.style.setProperty('--theme-nav-background', theme.nav.background);
  root.style.setProperty('--theme-nav-border', theme.nav.border);
  root.style.setProperty('--theme-nav-shadow', theme.nav.shadow);
  root.style.setProperty('--theme-nav-backdrop-filter', theme.nav.backdropFilter);
  root.style.setProperty('--theme-nav-active-background', theme.nav.activeBackground);
  root.style.setProperty('--theme-nav-active-text', theme.nav.activeText);

  // Modal
  root.style.setProperty('--theme-modal-background', theme.modal.background);
  root.style.setProperty('--theme-modal-border', theme.modal.border);
  root.style.setProperty('--theme-modal-shadow', theme.modal.shadow);
  root.style.setProperty('--theme-modal-backdrop-color', theme.modal.backdropColor);
  root.style.setProperty('--theme-modal-backdrop-blur', theme.modal.backdropBlur);

  // Dropdown
  root.style.setProperty('--theme-dropdown-background', theme.dropdown.background);
  root.style.setProperty('--theme-dropdown-background-hover', theme.dropdown.backgroundHover);
  root.style.setProperty('--theme-dropdown-border', theme.dropdown.border);
  root.style.setProperty('--theme-dropdown-shadow', theme.dropdown.shadow);

  // Cover fallback
  root.style.setProperty('--theme-cover-fallback-gradient', theme.coverFallback.gradient);
  root.style.setProperty('--theme-cover-fallback-text', theme.coverFallback.text);
  root.style.setProperty('--theme-cover-fallback-texture', theme.coverFallback.texture);
  root.style.setProperty('--theme-cover-fallback-texture-opacity', String(theme.coverFallback.textureOpacity));

  // Data attribute for theme-specific CSS selectors
  root.setAttribute('data-theme', theme.id);
  root.setAttribute('data-theme-mode', theme.mode);
  root.setAttribute('data-theme-category', theme.category);
}

/**
 * Theme Provider Component
 *
 * Wraps the application to provide theme context.
 * Manages theme state, page-specific backgrounds, and CSS variable injection.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  category,
  defaultThemeId,
  initialPageId = 'default',
}) => {
  // Initialize theme ID from props, localStorage, or defaults
  const [themeId, setThemeId] = useState<string>(() => {
    if (defaultThemeId && isValidThemeId(defaultThemeId)) {
      return defaultThemeId;
    }

    try {
      const storageKey = THEME_STORAGE_KEYS[category];
      const stored = localStorage.getItem(storageKey);
      if (stored && isValidThemeId(stored)) {
        // Verify stored theme matches category
        const storedTheme = getTheme(stored);
        if (storedTheme && storedTheme.category === category) {
          return stored;
        }
      }
    } catch (error) {
      console.warn('Failed to read theme from localStorage:', error);
    }

    return getDefaultTheme(category).id;
  });

  // Current page for page-specific backgrounds
  const [currentPageId, setCurrentPageId] = useState<PageId>(initialPageId);

  // Get current theme (with fallback)
  const theme = useMemo(() => {
    const resolvedTheme = getTheme(themeId);
    if (!resolvedTheme || resolvedTheme.category !== category) {
      return getDefaultTheme(category);
    }
    return resolvedTheme;
  }, [themeId, category]);

  // Get available themes for this category
  const availableThemes = useMemo(() => getThemesByCategory(category), [category]);

  // Get effective background for current page
  const getPageBackground = useCallback((): BackgroundTheme => {
    const pageOverride = theme.pageBackgrounds?.[currentPageId];

    if (pageOverride) {
      return {
        ...theme.background,
        gradient: pageOverride.gradient,
        gradientOverlay: pageOverride.gradientOverlay || theme.background.gradientOverlay,
        orbs: pageOverride.orbs || theme.background.orbs,
      };
    }

    return theme.background;
  }, [theme, currentPageId]);

  // Persist theme to localStorage
  useEffect(() => {
    try {
      const storageKey = THEME_STORAGE_KEYS[category];
      localStorage.setItem(storageKey, themeId);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }, [themeId, category]);

  // Apply CSS variables when theme or page changes
  useEffect(() => {
    const pageBackground = getPageBackground();
    applyThemeToCSSVariables(theme, pageBackground);
  }, [theme, getPageBackground]);

  // Set specific theme by ID
  const setTheme = useCallback(
    (newThemeId: string) => {
      const newTheme = getTheme(newThemeId);
      if (newTheme && newTheme.category === category) {
        setThemeId(newThemeId);
      } else {
        console.warn(`Theme "${newThemeId}" not found or not in category "${category}"`);
      }
    },
    [category]
  );

  // Set current page
  const setCurrentPage = useCallback((pageId: PageId) => {
    setCurrentPageId(pageId);
  }, []);

  const value: ThemeContextValue = useMemo(
    () => ({
      theme,
      themeId,
      currentPageId,
      setCurrentPage,
      getPageBackground,
      availableThemes,
      setTheme,
    }),
    [theme, themeId, currentPageId, setCurrentPage, getPageBackground, availableThemes, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeProvider;
